import React from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  LogOut,
  PanelRightOpen,
  User as UserIcon,
  Flame,
} from "lucide-react";
import VerifiedBadge from "../VerifiedBadge";
import { useVerificationStatus } from "../../hooks/useVerificationStatus";
import Tooltip from "../Tooltip/JobSeekerTooltip";
import { useUserProgress } from "../../hooks/useGamification";

interface HeaderProps {
  userType?: "jobseeker" | "hr" | "admin";
  onUtilityToggle?: () => void;
  isUtilityPanelOpen?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  userType,
  onUtilityToggle,
  isUtilityPanelOpen,
}) => {
  const { user, logout } = useAuth();
  const { isVerified } = useVerificationStatus();
  const { progress: userProgress, loading: progressLoading } = useUserProgress(user?.id);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-white/40 shadow-sm">
      <div className="flex items-center justify-between px-8 py-5">
        {/* Empty spacer for job seekers to push content to right */}
        {userType === "jobseeker" && <div className="flex-1"></div>}

        {/* Empty spacer for HR to push content to right */}
        {userType === "hr" && <div className="flex-1"></div>}

        <div className="flex items-center gap-4">
          {/* Show context panel toggle only for job seekers */}
          {userType === "jobseeker" && (
            <button
              type="button"
              className="hidden items-center gap-2 rounded-button bg-white/80 backdrop-blur-md border border-brand-indigo-200/50 px-4 py-2.5 text-sm font-semibold text-brand-indigo-700 transition-all hover:bg-white hover:border-brand-indigo-300 hover:shadow-md lg:flex"
              onClick={onUtilityToggle}
            >
              <PanelRightOpen
                className="h-4 w-4"
                strokeWidth={2}
              />
              {isUtilityPanelOpen ? "Hide context" : "Show context"}
            </button>
          )}

          {userType === "jobseeker" && (
            <Tooltip content="Your total streak points - Keep testing to earn more!">
              <div className="flex items-center gap-3 rounded-pill px-5 py-3 shadow-md transition-all hover:shadow-lg" style={{
                background: 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%)',
                border: '2px solid rgba(251, 146, 60, 0.3)'
              }}>
                <Flame className="h-6 w-6 text-orange-500" strokeWidth={2.5} />
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Streak Points</span>
                  <span className="text-xl font-bold text-orange-600">
                    {progressLoading ? "..." : userProgress?.totalXP || 0}
                  </span>
                </div>
              </div>
            </Tooltip>
          )}

          {userType === "hr" && (
            <button type="button" className="btn-primary">
              Create test from JD
            </button>
          )}

          <div className="flex items-center gap-4 rounded-glass backdrop-blur-md bg-white/80 border border-white/60 px-4 py-3 shadow-glass-sm transition-all hover:bg-white/90 hover:shadow-glass">
            <div className="flex flex-col items-end text-right">
              <span className="text-base font-semibold text-neutral-900">
                {user?.name ?? "Guest"}
              </span>
              <span className="text-sm capitalize text-brand-indigo-600 font-medium">
                {userType}
              </span>
            </div>
            <Tooltip content="View your profile">
              <div className="flex h-12 w-12 items-center justify-center rounded-full transition-all cursor-pointer" style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)'
              }}>
                <UserIcon className="h-6 w-6 text-white" strokeWidth={2} />
              </div>
            </Tooltip>
            <Tooltip content="Logout from your account">
              <button
                onClick={logout}
                className="flex h-10 w-10 items-center justify-center rounded-button text-neutral-400 transition-all hover:bg-red-50 hover:text-danger"
                aria-label="Logout"
              >
                <LogOut className="h-5 w-5" strokeWidth={2} />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {user?.userType === "jobseeker" && (
        <div className="flex items-center justify-between backdrop-blur-md bg-gradient-to-r from-white/60 to-white/40 border-t border-white/50 px-8 py-3">
          <div className="flex items-center gap-3 text-sm">
            <span className="font-semibold text-neutral-800 text-base">
              Skills badge
            </span>
            <VerifiedBadge isVerified={isVerified} size="sm" />
            <span className="text-neutral-600 font-medium">
              {isVerified ? "Verified" : "Awaiting verification"}
            </span>
          </div>
          {!isVerified && (
            <button type="button" className="btn-primary text-sm px-5 py-2.5">
              Start skills validation
            </button>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
